import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import get from 'lodash/get';
import { Observable } from 'rxjs';

import { CoreApiService } from '../../core-api.service';
import { InfoPropertyComponent } from './info-property.component';
import {
  CustomField,
  CustomFieldType,
  duplicateKeyValidator,
  infoToJson,
  invalidCharacterValidator,
  parseInfo,
  revealInvalid,
} from './info.utils';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { Container, ContainerInfo } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { firstValueFrom } from '../../shared/observable.utils';
import { generateElementId } from '../../shared/string.utils';
import { isEqual } from 'lodash';
import { ChangeManagerService } from '../../change-manager.service';
import { getContainerType } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';

@Component({
  selector: 'info-edit',
  styleUrls: ['./info-edit.component.scss'],
  templateUrl: './info-edit.component.html',
})
export class InfoEditComponent implements OnChanges {
  @Input() container: Container
  @Input() file: File
  @Input() info: ContainerInfo
  @Input() writable: boolean
  @Input() displayActionMenu = true
  @Input() displaySave = true
  @Input() changeManagerEnabled = true

  @Output() afterSave = new EventEmitter<ContainerInfo>()

  @ViewChild(InfoPropertyComponent, { static: true }) rootNode: InfoPropertyComponent

  infoForm = this.fb.group({
    type: 'root',
    key: null,
    value: this.buildArray([]),
  });

  constructor(
    private changeManager: ChangeManagerService,
    private fb: FormBuilder,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
  ) {
    if (this.changeManagerEnabled) {
      const containerType = this.container ? getContainerType(this.container) : null;
      const notificationPrefix =
        containerType === ContainerType.Session ? 'session.saveSession' :
        containerType === ContainerType.Subject ? 'subject.saveSubject' :
        'common.save';

      this.changeManager.registerForm('InfoEditComponent', {
        control: this.infoForm,
        save: () => this.saveFields(),
        reset: () => this.resetFields(),
        successMessageKey: `${notificationPrefix}.success`,
        errorMessageKey: `${notificationPrefix}.error`,
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const container = changes.container || changes.file;
    const containerChanged = container?.firstChange || get(container, 'previousValue._id') !== get(container, 'currentValue._id');
    if (containerChanged || (this.info && changes.info && !isEqual(changes.info.currentValue, changes.info.previousValue))) {
      this.resetFields();
    }
    if (this.displaySave && this.changeManagerEnabled) {
      this.displaySave = false;
    }
    if (!this.changeManagerEnabled) {
      this.changeManager.deregisterForm('InfoEditComponent');
    }
  }

  ngOnDestroy(): void {
    // Normally we would deregister the form here, but onDestroy fires after other components using info-edit, causing the change manager to stop working
  }

  get invalid(): boolean {
    return Boolean(this.infoForm?.invalid);
  }

  get dirty(): boolean {
    return Boolean(this.infoForm?.dirty);
  }

  get empty(): boolean {
    return this.infoForm ? (this.infoForm.get('value') as FormArray).length === 0 : true;
  }

  addField(type: CustomFieldType): void {
    this.rootNode.addMember(type);
  }

  async saveFields(): Promise<ContainerInfo | undefined> {
    if (this.invalid || !this.dirty) {
      this.infoForm.markAllAsTouched();
      revealInvalid([this.rootNode]);
      return undefined;
    }
    const json = infoToJson(this.infoForm.get('value').value);
    let updateObservable$: Observable<void>;
    // Check for a file first, since we have cases where both file and container could be set
    if (this.file) {
      updateObservable$ = this.coreApi.files.info.put(this.file.file_id, json);
    } else if (this.container) {
      updateObservable$ = this.coreApi.containers.info.patch(this.container._id, { replace: json });
    } else {
      throw new Error('No save method configured');
    }
    try {
      await firstValueFrom(updateObservable$);
      this.info = json;
      if (this.displaySave) {
        this.notifications.success('container.info.update.success');
      }
      this.afterSave.emit(this.info);
      return this.info;
    } catch (error) {
      this.logger.error(error);
      if (this.displaySave) {
        this.notifications.error('container.info.update.error');
      } else {
        throw error;
      }
      return undefined;
    }
  }

  resetFields(): void {
    const fields = parseInfo(this.info || {});
    this.infoForm.setControl('value', this.buildArray(fields));
    this.infoForm.markAsPristine();
    this.infoForm.updateValueAndValidity();
  }

  private buildArray(fields: CustomField[]): FormArray {
    return this.fb.array(fields.map(({ type, key, value }) => this.fb.group({
      uid: generateElementId,
      type,
      key: [key, type === 'array' ? [] : [Validators.required, invalidCharacterValidator]],
      value: Array.isArray(value) ? this.buildArray(value) : value,
    })), { validators: [duplicateKeyValidator] });
  }
}
